import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Send, Package, Building2, User, Car, MessageSquare,
  ClipboardList, ChevronDown, IndianRupee,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STATUSES = ["new", "submitted", "quoted", "closed"];
const SALES_STATUSES = [
  { k: "submitted", label: "Submitted" },
  { k: "contacted", label: "Contacted" },
  { k: "quoted", label: "Quoted" },
  { k: "won", label: "Won" },
  { k: "lost", label: "Lost" },
  { k: "closed", label: "Closed" },
];
const LOST_REASONS = [
  { k: "", label: "—" },
  { k: "price", label: "Price" },
  { k: "part_unavailable", label: "Part unavailable" },
  { k: "purchased_elsewhere", label: "Purchased elsewhere" },
  { k: "customer_cancelled", label: "Customer cancelled" },
  { k: "wrong_part", label: "Wrong part" },
  { k: "no_response", label: "No response" },
  { k: "delivery_time", label: "Delivery time" },
  { k: "other", label: "Other" },
];

const StatusPill = ({ status }) => {
  const map = {
    new: { bg: "#F7900915", fg: "#F79009", label: "New" },
    submitted: { bg: "#457b9d20", fg: "#457b9d", label: "Submitted" },
    quoted: { bg: "#12B76A15", fg: "#12B76A", label: "Quoted" },
    closed: { bg: "#66708515", fg: "#667085", label: "Closed" },
  }[status] || { bg: "#66708515", fg: "#667085", label: status || "—" };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: map.bg, color: map.fg }}>
      {map.label}
    </span>
  );
};

export default function AdminEnquiryDetail() {
  const { eid } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const canAct = ["platform_owner", "platform_admin", "sales_admin", "sales_manager"].includes(me?.admin_role);
  const canSales = ["platform_owner", "platform_admin", "sales_admin", "sales_manager", "support_admin"].includes(me?.admin_role);

  // Local sales-tracking form state
  const [sales, setSales] = useState({
    sales_status: "",
    assigned_salesperson: "",
    quote_value: "",
    sales_note: "",
    lost_reason: "",
  });
  const [savingSales, setSavingSales] = useState(false);

  const load = () => {
    api.get(`/admin/enquiries/${eid}`).then((r) => {
      setData(r.data);
      const e = r.data || {};
      setSales({
        sales_status: e.sales_status || "",
        assigned_salesperson: e.assigned_salesperson || "",
        quote_value: e.quote_value != null ? String(e.quote_value) : "",
        sales_note: e.sales_note || "",
        lost_reason: e.lost_reason || "",
      });
    }).catch((e) => {
      toast.error(formatApiError(e));
    });
  };
  useEffect(() => { load();   }, [eid]);

  const saveSales = async () => {
    setSavingSales(true);
    try {
      const payload = {
        sales_status: sales.sales_status || null,
        assigned_salesperson: sales.assigned_salesperson || "",
        quote_value: sales.quote_value === "" ? null : Number(sales.quote_value),
        sales_note: sales.sales_note || "",
        lost_reason: sales.lost_reason || "",
      };
      const r = await api.patch(`/admin/ops/enquiries/${eid}/sales`, payload);
      setData((prev) => ({ ...prev, ...(r.data.enquiry || {}) }));
      toast.success("Sales tracking saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSavingSales(false); }
  };

  const action = async (payload) => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/enquiries/${eid}/action`, payload);
      setData((prev) => ({ ...prev, ...r.data }));
      toast.success("Updated");
      if (payload.note) setNote("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="p-6 text-[13px] text-[#667085]">Loading enquiry…</div>;
  const items = Array.isArray(data.items) ? data.items : [];
  const notes = Array.isArray(data.notes) ? data.notes : [];

  return (
    <div className="space-y-5" data-testid="admin-enquiry-detail">
      <button
        onClick={() => nav("/admin/enquiries")}
        data-testid="admin-enquiry-back"
        className="text-[12px] text-[#457b9d] hover:text-[#e63946] font-semibold inline-flex items-center gap-1"
      >
        <ArrowLeft size={13} /> Back to enquiries
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-[#E4E7EC] p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Parts Enquiry</div>
            <h1 className="text-[26px] font-bold text-[#1d3557] leading-tight flex items-center gap-2" data-testid="admin-enquiry-number">
              <Send size={18} className="text-[#457b9d]" />
              {data.enquiry_number || data.id?.slice(0, 8)}
              <StatusPill status={data.status} />
            </h1>
            <div className="text-[11px] text-[#667085] font-mono mt-1">Enquiry ID: {data.id}</div>
            <div className="text-[11px] text-[#667085] font-mono mt-0.5">Created: {fmtDateTime(data.created_at)}</div>
          </div>
          {canAct && (
            <div className="flex items-center gap-2 flex-wrap">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="admin-enquiry-status-btn"
                    disabled={saving}
                    className="h-9 px-3 rounded-md border border-[#E4E7EC] bg-white text-[13px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5"
                  >
                    Change status <ChevronDown size={13} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-lg bg-white border border-[#E4E7EC] shadow-lg p-1 min-w-[160px]">
                  {STATUSES.map((s) => (
                    <DropdownMenuItem
                      key={s}
                      onClick={() => action({ status: s })}
                      data-testid={`admin-enquiry-status-${s}`}
                      className="cursor-pointer rounded-md text-[13px] hover:bg-[#F1FAEE] focus:bg-[#F1FAEE] px-2 py-2 uppercase tracking-wider text-[11px] font-bold text-[#1d3557]"
                    >
                      {s}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Items + linked JC */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-[#E4E7EC]" data-testid="admin-enquiry-items">
            <div className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between">
              <div>
                <h3 className="text-[15px] font-semibold text-[#1d3557]">Enquiry items</h3>
                <div className="text-[11px] text-[#667085] mt-0.5">{items.length} line(s)</div>
              </div>
              <Package size={16} className="text-[#457b9d]" />
            </div>
            {items.length === 0 ? (
              <div className="p-8 text-center text-[13px] text-[#667085]">No items on this enquiry.</div>
            ) : (
              <table className="w-full text-[13px]">
                <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Part</th>
                    <th className="text-left px-4 py-3 font-semibold">Category</th>
                    <th className="text-right px-4 py-3 font-semibold">Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1FAEE]">
                  {items.map((it, i) => (
                    <tr key={i}>
                      <td className="px-4 py-3">
                        <div className="text-[#1d3557] font-semibold">{it.name || it.sku || "—"}</div>
                        {it.sku && <div className="text-[11px] font-mono text-[#667085]">{it.sku}</div>}
                      </td>
                      <td className="px-4 py-3 text-[12px] text-[#667085]">{it.category || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-[#e63946] font-semibold">{it.qty || 1}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Notes / activity */}
          <div className="bg-white rounded-xl border border-[#E4E7EC]" data-testid="admin-enquiry-notes">
            <div className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between">
              <div>
                <h3 className="text-[15px] font-semibold text-[#1d3557]">Admin notes & activity</h3>
                <div className="text-[11px] text-[#667085] mt-0.5">Notes are audit-logged.</div>
              </div>
              <MessageSquare size={16} className="text-[#457b9d]" />
            </div>
            <div className="p-5 space-y-3">
              {notes.length === 0 ? (
                <div className="text-[13px] text-[#667085] text-center py-3">No notes yet.</div>
              ) : (
                notes.map((n, i) => (
                  <div key={i} className="border-l-2 border-[#e63946] pl-3">
                    <div className="text-[13px] text-[#1d3557]">{n.text}</div>
                    <div className="text-[11px] text-[#667085] mt-0.5 font-mono">{n.by_email} · {fmtDateTime(n.at)}</div>
                  </div>
                ))
              )}
              {canAct && (
                <div className="pt-2">
                  <Textarea
                    rows={3}
                    data-testid="admin-enquiry-note-input"
                    placeholder="Add an internal note (visible to SparesHub admins only)…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="rounded-md"
                  />
                  <Button
                    onClick={() => note.trim() && action({ note: note.trim() })}
                    disabled={!note.trim() || saving}
                    data-testid="admin-enquiry-note-save"
                    className="mt-2 h-9 bg-[#1d3557] hover:bg-[#152941] rounded-md text-white font-semibold text-[13px] px-3"
                  >
                    Save note
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: garage + linked JC */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-enquiry-garage">
            <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold flex items-center gap-1.5">
              <Building2 size={12} /> Garage
            </div>
            {data.garage ? (
              <>
                <Link
                  to={`/admin/garages/${data.garage.id}`}
                  className="text-[15px] font-semibold text-[#1d3557] hover:text-[#e63946] block mt-1.5"
                >
                  {data.garage.name}
                </Link>
                <div className="text-[11px] text-[#667085] mt-1 font-mono">{data.garage.city || "—"}</div>
                <div className="text-[11px] text-[#667085] mt-0.5 font-mono">{data.garage.phone || ""}</div>
              </>
            ) : (
              <div className="text-[13px] text-[#667085] mt-1">—</div>
            )}
          </div>

          {(data.customer_name || data.vehicle_reg) && (
            <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-enquiry-vehicle">
              <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold flex items-center gap-1.5">
                <Car size={12} /> Vehicle & customer
              </div>
              {data.customer_name && (
                <div className="text-[13px] text-[#1d3557] mt-1.5 flex items-center gap-1.5">
                  <User size={12} className="text-[#457b9d]" /> {data.customer_name}
                </div>
              )}
              {data.vehicle_reg && (
                <div className="text-[13px] font-mono text-[#1d3557] mt-1.5">{data.vehicle_reg}</div>
              )}
              {(data.vehicle_make || data.vehicle_model) && (
                <div className="text-[11px] text-[#667085] mt-0.5">
                  {[data.vehicle_make, data.vehicle_model].filter(Boolean).join(" ")}
                </div>
              )}
            </div>
          )}

          {data.job_card && (
            <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-enquiry-jobcard">
              <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold flex items-center gap-1.5">
                <ClipboardList size={12} /> Linked job card
              </div>
              <Link
                to={`/admin/job-cards/${data.job_card.id}`}
                data-testid="admin-enquiry-open-jc"
                className="text-[13px] font-semibold text-[#457b9d] hover:text-[#e63946] mt-1.5 block"
              >
                {data.job_card.job_number || data.job_card.id?.slice(0, 8)}
              </Link>
              <div className="text-[11px] text-[#457b9d] mt-1 uppercase tracking-wider font-semibold">
                {data.job_card.status?.replace(/_/g, " ")}
              </div>
            </div>
          )}

          {/* Sales Tracking */}
          {canSales && (
            <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-enquiry-sales">
              <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold flex items-center gap-1.5">
                <IndianRupee size={12} /> Sales tracking
              </div>
              <div className="mt-3 space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold mb-1">Sales Status</div>
                  <select
                    data-testid="enquiry-sales-status"
                    value={sales.sales_status}
                    onChange={(e) => setSales((s) => ({ ...s, sales_status: e.target.value }))}
                    className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] bg-white text-[13px]"
                  >
                    <option value="">— Not set —</option>
                    {SALES_STATUSES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold mb-1">Assigned Salesperson</div>
                  <Input
                    data-testid="enquiry-sales-person"
                    value={sales.assigned_salesperson}
                    onChange={(e) => setSales((s) => ({ ...s, assigned_salesperson: e.target.value }))}
                    placeholder="Salesperson name"
                    className="h-9 rounded-md text-[13px]"
                  />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold mb-1">Quote Value (₹)</div>
                  <Input
                    data-testid="enquiry-sales-quote"
                    type="number"
                    min="0"
                    step="1"
                    value={sales.quote_value}
                    onChange={(e) => setSales((s) => ({ ...s, quote_value: e.target.value }))}
                    placeholder="0"
                    className="h-9 rounded-md text-[13px] font-mono"
                  />
                </div>
                {sales.sales_status === "lost" && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold mb-1">Lost Reason</div>
                    <select
                      data-testid="enquiry-sales-lost-reason"
                      value={sales.lost_reason}
                      onChange={(e) => setSales((s) => ({ ...s, lost_reason: e.target.value }))}
                      className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] bg-white text-[13px]"
                    >
                      {LOST_REASONS.map((r) => <option key={r.k} value={r.k}>{r.label}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold mb-1">Sales Note</div>
                  <Textarea
                    data-testid="enquiry-sales-note"
                    rows={2}
                    value={sales.sales_note}
                    onChange={(e) => setSales((s) => ({ ...s, sales_note: e.target.value }))}
                    placeholder="e.g., Customer wants sample photo before quoting"
                    className="rounded-md text-[13px]"
                  />
                </div>
                <Button
                  onClick={saveSales}
                  disabled={savingSales}
                  data-testid="enquiry-sales-save"
                  className="w-full h-9 bg-[#e63946] hover:bg-[#c92e3b] rounded-md text-white font-semibold text-[13px]"
                >
                  {savingSales ? "Saving…" : "Save Sales Tracking"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
